/**
 * Nordkyn Heritage storefront enhancements (English-only, USD).
 * Renders the TypeSafe cart upsell and semantic search callout, and tidies
 * placeholder store names on policy pages. Prices come from Shopify.
 */
(() => {
  // Catalog amounts below are in the shop currency; Shopify.currency carries the
  // active presentment currency and rate.
  function formatMoney(amount) {
    const currency = (window.Shopify && window.Shopify.currency) || {};
    const code = currency.active || 'USD';
    const rate = Number(currency.rate) || 1;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount * rate);
  }

  // Replaces {{34}}-style placeholders with the amount in the active currency
  function fillPrices(text) {
    return text.replace(/\{\{(\d+(?:\.\d+)?)\}\}/g, (m, amount) => formatMoney(Number(amount)));
  }

  const SEMANTIC_INTENT_MAP = [
    {
      keywords: ['freezing', 'freeze', 'cold', 'wind', 'layering', 'chill', 'venting', 'vapor', 'polar wind', 'arctic wind'],
      intent: 'Extreme Wind-Chill & Thermal Layering Protocols',
      handle: 'arctic-coastal-sub-zero-field-manual-navigation-severe-weather-protocols-expedition-layering',
      title: 'Manual 01: Arctic & Coastal Sub-Zero Field Manual: Navigation, Severe Weather Protocols & Expedition Layering',
      chapter: 'Chapter 3: Expedition Layering & Moisture Management',
      desc: 'Engineering specifications for three-phase moisture transport, vapor barrier liners, and mitigating severe wind-chill heat loss.',
      priceUSD: 34.00
    },
    {
      keywords: ['navigation', 'gps', 'compass', 'lost', 'whiteout', 'route', 'dead reckoning', 'dip', 'magnetic', 'declination', 'bearing'],
      intent: 'Polar Navigation & Non-Electronic Dead Reckoning',
      handle: 'arctic-coastal-sub-zero-field-manual-navigation-severe-weather-protocols-expedition-layering',
      title: 'Manual 01: Arctic & Coastal Sub-Zero Field Manual: Navigation, Severe Weather Protocols & Expedition Layering',
      chapter: 'Chapter 1: Coastal Geomagnetic Dip & Polar Navigation',
      desc: 'Dead-reckoning doctrine, magnetic dip compensation above 70°N, and waypoint leapfrogging when GPS electronics fail.',
      priceUSD: 34.00
    },
    {
      keywords: ['shelter', 'snow cave', 'snowcave', 'cave', 'blizzard', 'quinzee', 'bivouac', 'asphyxiation', 'trench', 'deadman', 'storm anchor'],
      intent: 'Emergency Snow Cave & Gale Storm Shelter Engineering',
      handle: 'manual-02-high-latitude-storm-shelter-snow-cave-engineering-guide',
      title: 'Manual 02: High-Latitude Storm Shelter & Snow Cave Engineering Guide',
      chapter: 'Chapter 2: Quinzee Sintering & Chapter 4: Cold Wells',
      desc: 'Structural snowpack physics, powder snow sintering, thermal cold sink stratification, and CO2 ventilation geometry in severe blizzards.',
      priceUSD: 34.00
    },
    {
      keywords: ['hypothermia', 'frostbite', 'hydration', 'water', 'snowmelt', 'calories', 'medicine', 'first aid', 'rewarming', 'stove fuel'],
      intent: 'Systemic Hypothermia Rewarming & Polar Hydration Protocols',
      handle: 'manual-03-polar-expedition-hydration-caloric-planning-cold-medicine',
      title: 'Manual 03: Polar Expedition Hydration, Caloric Planning & Cold Medicine',
      chapter: 'Chapter 4: Frostbite Rewarming & Chapter 5: Hypothermia Protocols (Swiss Staging HT I-IV)',
      desc: 'Clinical field staging for freezing injuries, avoiding core rewarming shock, osmolality management, and snowmelt fuel budgeting.',
      priceUSD: 34.00
    },
    {
      keywords: ['bundle', 'complete', 'suite', 'expedition', 'traverse', 'all manuals', 'collection', 'training', 'multiday', 'unsupported'],
      intent: 'Comprehensive Polar Traverse Doctrine & Multi-Hazard Readiness',
      handle: 'the-complete-arctic-field-operations-collection',
      title: 'The Complete Arctic Field Operations Collection',
      chapter: 'All 3 Volumes (Navigation, Shelters, Cold Medicine) + Printable Pocket Cards',
      desc: 'The master expedition suite uniting full navigational doctrine, structural snow cave engineering, and clinical cold weather medicine with printable emergency checklists.',
      priceUSD: 76.00,
      isBundle: true
    }
  ];

  // ==========================================
  // TypeSafe Intelligent Cart Upsell & Bundle Recommender
  // ==========================================
  const CART_SYNERGY_MATRIX = {
    // Scenario 1: Only Manual 01 in cart -> Recommend Manual 02 (Storm Shelters) with Suite Upgrade teaser
    'manual-01-only': {
      type: 'companion_manual',
      handle: 'manual-02-high-latitude-storm-shelter-snow-cave-engineering-guide',
      variantId: '49129903259838',
      title: 'Manual 02: High-Latitude Storm Shelter Guide',
      badge: '🧭 Highest Synergy Companion',
      headline: 'Complete Your Survival Shelter Protocol',
      reason: 'Navigation plots your route across sub-zero plateaus; Manual 02 ensures you can construct structural snow caves and deadman gale windbreaks when whiteouts hit.',
      priceUSD: 34.00,
      bundleTeaser: 'Or upgrade to the Complete 3-Volume Suite for {{76}} (Save {{26}})',
      bundleHandle: 'the-complete-arctic-field-operations-collection',
      bundleVariantId: '49129903587518'
    },

    // Scenario 2: Only Manual 02 in cart -> Recommend Manual 03 (Cold Medicine & Hydration)
    'manual-02-only': {
      type: 'companion_manual',
      handle: 'manual-03-polar-expedition-hydration-caloric-planning-cold-medicine',
      variantId: '49129903325374',
      title: 'Manual 03: Polar Expedition Hydration, Caloric Planning & Cold Medicine',
      badge: '🧭 Critical Expedition Companion',
      headline: 'Prevent Systemic Hypothermia Inside Shelters',
      reason: 'Once sheltered, metabolic heat loss, cold-induced diuresis, and snowmelt carbon monoxide risks dictate survival. Manual 03 provides clinical field protocols.',
      priceUSD: 34.00,
      bundleTeaser: 'Or upgrade to the Complete 3-Volume Suite for {{76}} (Save {{26}})',
      bundleHandle: 'the-complete-arctic-field-operations-collection',
      bundleVariantId: '49129903587518'
    },

    // Scenario 3: Only Manual 03 in cart -> Recommend Manual 01 (Navigation & Weather Protocols)
    'manual-03-only': {
      type: 'companion_manual',
      handle: 'arctic-coastal-sub-zero-field-manual-navigation-severe-weather-protocols-expedition-layering',
      variantId: '49125152915646',
      title: 'Manual 01: Arctic & Coastal Sub-Zero Field Manual',
      badge: '🧭 Foundational Route Companion',
      headline: 'Master High-Latitude Navigation & Gale Protocols',
      reason: 'Medical readiness requires navigation mastery: calculate polar magnetic declination, dead-reckoning in whiteouts, and multi-phase moisture layering.',
      priceUSD: 34.00,
      bundleTeaser: 'Or upgrade to the Complete 3-Volume Suite for {{76}} (Save {{26}})',
      bundleHandle: 'the-complete-arctic-field-operations-collection',
      bundleVariantId: '49129903587518'
    },

    // Scenario 4: Any two individual manuals in cart -> Upgrade to Complete 3-Volume Suite!
    'two-manuals': {
      type: 'bundle_upgrade',
      handle: 'the-complete-arctic-field-operations-collection',
      variantId: '49129903587518',
      title: 'The Complete Arctic Field Operations Collection',
      badge: '⭐ Best Value • 25% Suite Savings',
      headline: 'Upgrade to the Complete 3-Volume Master Suite',
      reason: 'You have 2 individual manuals selected ({{68}} total). For only {{8}} more, unlock the Complete 3-Volume Suite ({{76}}) including the 3rd manual plus printable emergency pocket cards (saves {{26}}).',
      priceUSD: 76.00
    },

    // Scenario 5: Outerwear or gear only in cart -> Recommend Manual 01 (foundational doctrine)
    'outerwear-only': {
      type: 'companion_manual',
      handle: 'arctic-coastal-sub-zero-field-manual-navigation-severe-weather-protocols-expedition-layering',
      variantId: '49125152915646',
      title: 'Manual 01: Arctic & Coastal Sub-Zero Field Manual',
      badge: '📖 Essential Field Doctrine',
      headline: 'Equip the Doctrine for Your Expedition Gear',
      reason: 'Technical gear reaches full capability when paired with field doctrine: 3-phase vapor layering, blizzard survival, and polar dead-reckoning navigation.',
      priceUSD: 34.00
    },

    // Scenario 6: Complete Suite already in cart -> Recommend Glacier Shield Mittens
    'bundle-in-cart': {
      type: 'complementary_gear',
      handle: 'glacier-shield-insulated-mittens',
      variantId: '49117154410686',
      title: 'Glacier Shield Insulated Mittens',
      badge: '🧤 Recommended Sub-Zero Gear',
      headline: 'Protect Your Extremities Down to -35°C',
      reason: 'You possess the complete digital doctrine suite. Pair your technical knowledge with PrimaLoft Gold expedition mittens and removable wool liners.',
      priceUSD: 95.00
    }
  };

  function getCartItemsFromDom() {
    const items = [];
    document.querySelectorAll('.cart-item, tr.cart-item, #CartDrawer-CartItems tr').forEach((row) => {
      const link = row.querySelector('.cart-item__name, a.cart-item__name, a[href*="/products/"]');
      const href = link ? link.getAttribute('href') || '' : '';
      const m = href.match(/\/products\/([a-zA-Z0-9_-]+)/);
      if (m && m[1]) {
        items.push({
          handle: m[1],
          title: link.textContent.trim()
        });
      }
    });
    return items;
  }

  function resolveCartUpsell(cartItems) {
    if (!cartItems || cartItems.length === 0) return null;

    const handles = new Set(cartItems.map((i) => i.handle));
    const hasBundle = handles.has('the-complete-arctic-field-operations-collection');
    const hasM1 = handles.has('arctic-coastal-sub-zero-field-manual-navigation-severe-weather-protocols-expedition-layering');
    const hasM2 = handles.has('manual-02-high-latitude-storm-shelter-snow-cave-engineering-guide');
    const hasM3 = handles.has('manual-03-polar-expedition-hydration-caloric-planning-cold-medicine');

    const manualCount = [hasM1, hasM2, hasM3].filter(Boolean).length;

    // Rule 1: Two individual manuals in cart -> Upgrade to Complete 3-Volume Suite!
    if (manualCount >= 2 && !hasBundle) {
      return CART_SYNERGY_MATRIX['two-manuals'];
    }

    // Rule 2: Single manual in cart -> High-synergy companion manual
    if (hasM1 && !hasM2 && !hasM3 && !hasBundle) {
      return CART_SYNERGY_MATRIX['manual-01-only'];
    }
    if (hasM2 && !hasM1 && !hasM3 && !hasBundle) {
      return CART_SYNERGY_MATRIX['manual-02-only'];
    }
    if (hasM3 && !hasM1 && !hasM2 && !hasBundle) {
      return CART_SYNERGY_MATRIX['manual-03-only'];
    }

    // Rule 3: Complete bundle in cart -> Complementary Gear (Glacier Shield Mittens)
    if (hasBundle || manualCount === 3) {
      if (!handles.has('glacier-shield-insulated-mittens')) {
        return CART_SYNERGY_MATRIX['bundle-in-cart'];
      }
      return null;
    }

    // Rule 4: Outerwear or gear only in cart -> Recommend foundational Manual 01
    if (manualCount === 0 && !hasBundle && cartItems.length > 0) {
      return CART_SYNERGY_MATRIX['outerwear-only'];
    }

    return null;
  }

  // Quick 1-click Add-To-Cart handler for Cart Drawer
  window.nordkynAddUpsellToCart = async function(variantId, btnElement) {
    if (!variantId) return;
    const originalText = btnElement ? btnElement.innerHTML : '';
    if (btnElement) {
      btnElement.disabled = true;
      btnElement.innerHTML = '<span style="display: inline-block; animation: spin 0.8s linear infinite;">⏳</span> Adding...';
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!response.ok) throw new Error('Add to cart failed');

      if (btnElement) {
        btnElement.innerHTML = '✓ Added!';
        btnElement.style.background = '#2A4035';
      }

      // Refresh Dawn's cart drawer and icon bubble in place
      if (window.routes && window.routes.cart_url) {
        fetch(`${window.routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
          .then((r) => r.json())
          .then((parsed) => {
            const cartDrawer = document.querySelector('cart-drawer');
            if (parsed['cart-drawer'] && cartDrawer) {
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = parsed['cart-drawer'];
              const newInner = tempDiv.querySelector('.drawer__inner');
              const curInner = cartDrawer.querySelector('.drawer__inner');
              if (newInner && curInner) curInner.innerHTML = newInner.innerHTML;
            }
            const iconBubble = document.getElementById('cart-icon-bubble');
            if (parsed['cart-icon-bubble'] && iconBubble) iconBubble.innerHTML = parsed['cart-icon-bubble'];
            render();
          }).catch(() => {
            window.location.reload();
          });
      } else {
        window.dispatchEvent(new CustomEvent('cart:updated'));
        setTimeout(render, 300);
      }
    } catch (err) {
      console.error('Failed to add upsell item to cart:', err);
      if (btnElement) {
        btnElement.disabled = false;
        btnElement.innerHTML = originalText;
      }
    }
  };

  function removeUpsellWidget() {
    const existing = document.getElementById('nordkyn-cart-upsell-container');
    if (existing) existing.remove();
  }

  function renderCartUpsellWidget() {
    const drawerWrapper = document.getElementById('CartDrawer-CartItems') ||
      document.querySelector('cart-drawer-items') ||
      document.querySelector('#main-cart-items');
    if (!drawerWrapper) return;

    const items = getCartItemsFromDom();
    const upsell = items.length > 0 ? resolveCartUpsell(items) : null;
    if (!upsell) {
      removeUpsellWidget();
      return;
    }

    const bundleTeaser = upsell.bundleTeaser ? `
      <div style="margin-top: 10px; padding-top: 8px; border-top: 1px dashed rgba(200, 125, 85, 0.3); font-size: 1.35rem; color: #55625D; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px;">
        <span>${fillPrices(upsell.bundleTeaser)}</span>
        <button type="button" onclick="window.nordkynAddUpsellToCart('${upsell.bundleVariantId}', this)" style="background: none; border: 1px solid #C87D55; color: #C87D55; padding: 4px 10px; border-radius: 4px; font-size: 1.25rem; font-weight: 600; cursor: pointer; text-transform: uppercase;">
          Add Suite
        </button>
      </div>
    ` : '';

    const html = `
      <div class="nordkyn-cart-upsell-card" style="background: #FAF9F6; border: 1.5px solid rgba(200, 125, 85, 0.35); border-radius: 10px; padding: 14px 16px; margin: 14px 0 10px; box-shadow: 0 4px 16px rgba(28, 43, 36, 0.05); text-align: left;">
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; flex-wrap: wrap; gap: 4px;">
          <span style="display: inline-flex; align-items: center; gap: 5px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 1.25rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #C87D55; background: rgba(200, 125, 85, 0.12); padding: 3px 8px; border-radius: 4px;">
            ${upsell.badge}
          </span>
          <span style="font-size: 1.25rem; color: #73807B; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;">
            TypeSafe System One
          </span>
        </div>
        <h4 style="font-family: var(--font-heading-family, 'Cinzel', serif); font-size: 1.6rem; font-weight: 700; color: #1C2B24; margin: 0 0 4px; line-height: 1.3;">
          ${upsell.headline}
        </h4>
        <div style="font-size: 1.45rem; font-weight: 600; color: #1C2B24; margin-bottom: 6px;">
          <a href="/products/${upsell.handle}" style="color: #1C2B24; text-decoration: underline; text-underline-offset: 3px;">${upsell.title}</a>
        </div>
        <p style="font-size: 1.4rem; line-height: 1.45; color: #55625D; margin: 0 0 10px;">
          ${fillPrices(upsell.reason)}
        </p>
        <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap;">
          <div style="font-size: 1.6rem; font-weight: 700; color: #1C2B24; font-family: 'Plus Jakarta Sans', sans-serif;">
            ${formatMoney(upsell.priceUSD)}
          </div>
          <button type="button" onclick="window.nordkynAddUpsellToCart('${upsell.variantId}', this)" class="button button--secondary" style="background: #1C2B24; color: #FFFFFF; border: none; padding: 8px 16px; border-radius: 6px; font-size: 1.35rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; cursor: pointer; transition: all 0.2s ease;">
            + Add to Cart
          </button>
        </div>
        ${bundleTeaser}
      </div>
    `;

    const existingWidget = document.getElementById('nordkyn-cart-upsell-container');
    if (existingWidget) {
      if (existingWidget.dataset.upsellHandle !== upsell.handle) {
        existingWidget.dataset.upsellHandle = upsell.handle;
        existingWidget.innerHTML = html;
      }
      return;
    }

    const widget = document.createElement('div');
    widget.id = 'nordkyn-cart-upsell-container';
    widget.dataset.upsellHandle = upsell.handle;
    widget.innerHTML = html;

    const dedicatedMount = document.getElementById('nordkyn-cart-upsell-mount');
    if (dedicatedMount) {
      dedicatedMount.innerHTML = '';
      dedicatedMount.appendChild(widget);
    } else {
      const insertTarget = drawerWrapper.querySelector('#CartDrawer-CartItems') ||
        drawerWrapper.querySelector('.drawer__contents') ||
        drawerWrapper;
      insertTarget.appendChild(widget);
    }
  }

  function renderSemanticSearchCallout() {
    if (!window.location.pathname.includes('/search')) return;
    const rawQuery = (new URLSearchParams(window.location.search).get('q') || '').trim();
    if (!rawQuery) return;
    if (document.getElementById('nordkyn-semantic-callout')) return;

    const lowerQ = rawQuery.toLowerCase();
    const words = lowerQ.split(/\s+/).filter(Boolean);
    const match = SEMANTIC_INTENT_MAP.find((entry) =>
      entry.keywords.some((kw) => lowerQ.includes(kw) || words.some((w) => kw.includes(w) || w.includes(kw)))
    );
    if (!match) return;

    const container = document.querySelector('.template-search__header') || document.querySelector('.template-search');
    if (!container) return;

    const suiteNotice = match.isBundle ? '' : `
      <div style="margin-top: 18px; padding-top: 14px; border-top: 1px dashed rgba(200, 125, 85, 0.35); font-size: 1.35rem; color: #55625D; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;">
        <span>⭐ <strong>Expedition Suite Recommendation:</strong> Preparing for an unsupported multi-day traverse? The <a href="/products/the-complete-arctic-field-operations-collection" style="color: #C87D55; font-weight: 600; text-decoration: underline;">Complete Arctic Field Operations Collection</a> (${formatMoney(76)}) combines all 3 volumes.</span>
      </div>
    `;

    const card = document.createElement('div');
    card.id = 'nordkyn-semantic-callout';
    card.style.cssText = 'margin: 28px auto 36px; max-width: 820px; background: #FFFFFF; border: 1.5px solid rgba(200, 125, 85, 0.4); border-radius: 12px; padding: 24px 28px; box-shadow: 0 10px 30px rgba(28, 43, 36, 0.07); text-align: left; position: relative;';
    card.innerHTML = `
      <div class="nordkyn-semantic-header-bar" style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; flex-wrap: wrap; gap: 8px;">
        <span style="display: inline-flex; align-items: center; gap: 6px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 1.25rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #C87D55; background: rgba(200, 125, 85, 0.12); padding: 4px 12px; border-radius: 20px; border: 1px solid rgba(200, 125, 85, 0.3);">
          🧭 TYPESAFE SYSTEM ONE • SEMANTIC EXPEDITION MATCH
        </span>
        <span style="font-size: 1.25rem; color: #73807B; font-family: 'Plus Jakarta Sans', sans-serif;">
          Calibrated Precision (Jev 1.12)
        </span>
      </div>
      <h3 style="font-family: var(--font-heading-family, 'Cinzel', serif); font-size: 1.75rem; font-weight: 700; color: #1C2B24; margin: 0 0 8px; line-height: 1.3;">
        Intent Resolved: <span style="color: #C87D55;">${match.intent}</span>
      </h3>
      <p style="font-size: 1.5rem; line-height: 1.55; color: #4B5551; margin: 0 0 18px;">
        Shopify literal keyword search returned 0 direct results for “${rawQuery}”. TypeSafe System One evaluated catalog technical doctrine and resolved your situational query to the authoritative field publication:
      </p>
      <div class="nordkyn-semantic-result-card" style="background: #FAF9F6; border: 1px solid #ECEAE5; border-radius: 8px; padding: 20px; display: flex; align-items: center; justify-content: space-between; gap: 24px; flex-wrap: wrap;">
        <div style="flex: 1 1 360px;">
          <span style="display: inline-block; font-size: 1.25rem; font-weight: 700; letter-spacing: 0.1em; color: #1C2B24; background: rgba(28, 43, 36, 0.08); padding: 2px 8px; border-radius: 4px; margin-bottom: 8px;">
            🥇 PRIMARY TECHNICAL MANUAL
          </span>
          <h4 style="font-family: var(--font-heading-family, 'Cinzel', serif); font-size: 1.65rem; font-weight: 700; margin: 0 0 8px; line-height: 1.35;">
            <a href="/products/${match.handle}" style="color: #1C2B24; text-decoration: none;">${match.title}</a>
          </h4>
          <div style="font-size: 1.45rem; font-weight: 600; color: #C87D55; margin-bottom: 6px;">
            📖 Matched Doctrine: <span>${match.chapter}</span>
          </div>
          <p style="font-size: 1.4rem; line-height: 1.5; color: #55625D; margin: 0;">
            ${match.desc}
          </p>
        </div>
        <div style="flex: 0 0 auto; display: flex; flex-direction: column; align-items: flex-end; gap: 10px; min-width: 160px;">
          <div style="font-size: 1.75rem; font-weight: 700; color: #1C2B24; font-family: 'Plus Jakarta Sans', sans-serif;">
            ${formatMoney(match.priceUSD)}
          </div>
          <a href="/products/${match.handle}" class="button button--primary" style="background: #1C2B24; color: #FFFFFF; padding: 12px 22px; border-radius: 8px; font-weight: 600; font-size: 1.45rem; letter-spacing: 0.06em; text-transform: uppercase; text-decoration: none; display: inline-block; text-align: center; border: none; box-shadow: 0 4px 12px rgba(28, 43, 36, 0.15);">
            View Field Manual
          </a>
        </div>
      </div>
      ${suiteNotice}
    `;

    const searchFormWrap = container.querySelector('.template-search__search') || container.querySelector('predictive-search');
    if (searchFormWrap && searchFormWrap.parentNode) {
      searchFormWrap.parentNode.insertBefore(card, searchFormWrap.nextSibling);
    } else {
      container.appendChild(card);
    }
  }

  // Policy pages still carry the placeholder store name "loja1"
  function replacePlaceholderStoreName() {
    document.querySelectorAll('.shopify-policy__body .rte, .shopify-policy__title').forEach((el) => {
      if (el.innerHTML.includes('loja1')) {
        el.innerHTML = el.innerHTML.replace(/loja1/g, 'Nordkyn Heritage');
      }
    });
  }

  // Container observers; records produced by our own render are discarded so
  // only Dawn's cart swaps trigger a re-render.
  const cartObservers = [];

  function render() {
    renderCartUpsellWidget();
    renderSemanticSearchCallout();
    replacePlaceholderStoreName();
    cartObservers.forEach((obs) => obs.takeRecords());
  }

  let pendingRender = null;
  function scheduleRender() {
    if (pendingRender !== null) return;
    pendingRender = setTimeout(() => {
      pendingRender = null;
      render();
    }, 40);
  }

  function observeCartContainers() {
    if (typeof MutationObserver === 'undefined') return;
    ['CartDrawer', 'main-cart-items'].forEach((id) => {
      const el = document.getElementById(id);
      if (!el || el.dataset.nordkynObserved) return;
      el.dataset.nordkynObserved = 'true';
      const observer = new MutationObserver(scheduleRender);
      observer.observe(el, { childList: true, subtree: true });
      cartObservers.push(observer);
    });
  }

  function init() {
    render();
    observeCartContainers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Dawn publishes cartUpdate after add-to-cart and quantity changes
  if (window.subscribe && window.PUB_SUB_EVENTS) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, scheduleRender);
  }
})();
